/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef } from 'react';
import logger from '../lib/logger';

/**
 * HeroLineArt — animated CAD/blueprint-style line drawings.
 * Each variant emulates a real, dense engineering drawing with:
 *  - dimension chains + extension lines + arrowheads
 *  - leader lines + callouts + bubble references
 *  - hatching / section indicators
 *  - title blocks with project metadata
 *  - grid references, north arrows, scale bars
 *  - mixed linetypes (continuous / dashed / dash-dot)
 */

const W = 800;
const H = 600;

// ---------- shared SVG helpers ------------------------------------------
const Arrow = ({ x, y, angle = 0, size = 5 }) => (
  <polygon
    points={`0,0 ${-size},${-size / 2} ${-size},${size / 2}`}
    fill="currentColor"
    transform={`translate(${x} ${y}) rotate(${angle})`}
  />
);

const DimH = ({ x1, x2, y, label }) => (
  <g>
    {/* extension lines */}
    <line x1={x1} y1={y - 12} x2={x1} y2={y + 4} />
    <line x1={x2} y1={y - 12} x2={x2} y2={y + 4} />
    {/* dim line */}
    <line x1={x1} y1={y} x2={x2} y2={y} />
    <Arrow x={x1 + 5} y={y} angle={180} />
    <Arrow x={x2 - 5} y={y} angle={0} />
    <text x={(x1 + x2) / 2} y={y - 4} fontSize="9" fill="currentColor" stroke="none" opacity="0.8" textAnchor="middle">{label}</text>
  </g>
);

const DimV = ({ y1, y2, x, label }) => (
  <g>
    <line x1={x - 12} y1={y1} x2={x + 4} y2={y1} />
    <line x1={x - 12} y1={y2} x2={x + 4} y2={y2} />
    <line x1={x} y1={y1} x2={x} y2={y2} />
    <Arrow x={x} y={y1 + 5} angle={-90} />
    <Arrow x={x} y={y2 - 5} angle={90} />
    <text x={x - 6} y={(y1 + y2) / 2} fontSize="9" fill="currentColor" stroke="none" opacity="0.8" textAnchor="end" transform={`rotate(-90 ${x - 6} ${(y1 + y2) / 2})`}>{label}</text>
  </g>
);

const Bubble = ({ x, y, label, r = 10 }) => (
  <g>
    <circle cx={x} cy={y} r={r} fill="none" />
    <text x={x} y={y + 3} fontSize="10" fill="currentColor" stroke="none" opacity="0.85" textAnchor="middle">{label}</text>
  </g>
);

const NorthArrow = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle r="22" fill="none" />
    <polygon points="0,-22 6,12 0,4 -6,12" fill="currentColor" stroke="none" />
    <text x="0" y="-26" fontSize="10" fill="currentColor" stroke="none" opacity="0.7" textAnchor="middle">N</text>
  </g>
);

const ScaleBar = ({ x, y, label = '0   1m   2m   3m' }) => (
  <g>
    <line x1={x} y1={y} x2={x + 120} y2={y} />
    {[0, 40, 80, 120].map(o => <line key={o} x1={x + o} y1={y - 5} x2={x + o} y2={y + 5} />)}
    <rect x={x} y={y - 4} width="40" height="8" fill="currentColor" opacity="0.7" />
    <rect x={x + 80} y={y - 4} width="40" height="8" fill="currentColor" opacity="0.7" />
    <text x={x} y={y + 18} fontSize="9" fill="currentColor" stroke="none" opacity="0.6">{label}</text>
  </g>
);

const TitleBlock = ({ project, sheet, scale, rev = 'C' }) => (
  <g>
    <rect x="500" y="510" width="260" height="60" fill="none" />
    <line x1="500" y1="528" x2="760" y2="528" />
    <line x1="595" y1="510" x2="595" y2="570" />
    <line x1="690" y1="510" x2="690" y2="570" />
    <text x="508" y="523" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" letterSpacing="1">LAND DRAFTERS</text>
    <text x="508" y="545" fontSize="9" fill="currentColor" stroke="none" opacity="0.85">{project}</text>
    <text x="508" y="560" fontSize="8" fill="currentColor" stroke="none" opacity="0.55">{sheet} · {scale}</text>
    <text x="600" y="545" fontSize="8" fill="currentColor" stroke="none" opacity="0.6">DRAWN</text>
    <text x="600" y="560" fontSize="9" fill="currentColor" stroke="none" opacity="0.85">L.D.</text>
    <text x="695" y="545" fontSize="8" fill="currentColor" stroke="none" opacity="0.6">REV</text>
    <text x="695" y="560" fontSize="9" fill="currentColor" stroke="none" opacity="0.85">{rev}</text>
  </g>
);

const Grid = ({ cols = [80, 200, 320, 440, 560, 680], rows = [120, 240, 360, 480] }) => (
  <g opacity="0.18">
    {cols.map((x, i) => (
      <g key={`gc${i}`}>
        <line x1={x} y1="60" x2={x} y2="500" strokeDasharray="2 6" />
        <circle cx={x} cy="50" r="9" fill="none" opacity="0.7" />
        <text x={x} y="53" fontSize="9" fill="currentColor" stroke="none" textAnchor="middle" opacity="0.7">{String.fromCharCode(65 + i)}</text>
      </g>
    ))}
    {rows.map((y, i) => (
      <g key={`gr${i}`}>
        <line x1="40" y1={y} x2="760" y2={y} strokeDasharray="2 6" />
        <circle cx="30" cy={y} r="9" fill="none" opacity="0.7" />
        <text x="30" y={y + 3} fontSize="9" fill="currentColor" stroke="none" textAnchor="middle" opacity="0.7">{i + 1}</text>
      </g>
    ))}
  </g>
);

// ---------- variants ---------------------------------------------------

const VariantDrafting = () => (
  <g>
    <Grid />
    {/* Floor plan — multi-room with thick exterior wall */}
    <rect x="120" y="120" width="540" height="320" fill="none" strokeWidth="3" />
    <rect x="128" y="128" width="524" height="304" fill="none" />
    {/* Interior walls */}
    <line x1="320" y1="120" x2="320" y2="280" strokeWidth="2" />
    <line x1="320" y1="280" x2="520" y2="280" strokeWidth="2" />
    <line x1="520" y1="120" x2="520" y2="280" strokeWidth="2" />
    <line x1="120" y1="280" x2="320" y2="280" strokeWidth="2" />
    <line x1="420" y1="280" x2="420" y2="440" strokeWidth="2" />
    {/* Doors with arc */}
    <path d="M 260 280 A 30 30 0 0 1 290 250" fill="none" />
    <line x1="260" y1="280" x2="290" y2="280" strokeWidth="0.5" opacity="0.4" />
    <path d="M 460 280 A 25 25 0 0 1 485 305" fill="none" />
    <path d="M 320 200 A 24 24 0 0 1 296 224" fill="none" />
    {/* Windows */}
    <line x1="160" y1="120" x2="240" y2="120" strokeWidth="4" opacity="0.3" />
    <line x1="160" y1="120" x2="240" y2="120" strokeDasharray="3 3" />
    <line x1="380" y1="120" x2="460" y2="120" strokeWidth="4" opacity="0.3" />
    <line x1="380" y1="120" x2="460" y2="120" strokeDasharray="3 3" />
    <line x1="660" y1="180" x2="660" y2="260" strokeWidth="4" opacity="0.3" />
    <line x1="660" y1="180" x2="660" y2="260" strokeDasharray="3 3" />
    <line x1="540" y1="440" x2="620" y2="440" strokeWidth="4" opacity="0.3" />
    <line x1="540" y1="440" x2="620" y2="440" strokeDasharray="3 3" />

    {/* Furniture */}
    <rect x="150" y="200" width="100" height="50" fill="none" />
    <rect x="155" y="205" width="90" height="40" fill="none" opacity="0.4" />
    <ellipse cx="200" cy="350" rx="35" ry="22" fill="none" />
    <rect x="350" y="160" width="60" height="100" fill="none" />
    <rect x="355" y="170" width="50" height="20" fill="none" opacity="0.4" />
    <rect x="560" y="160" width="80" height="100" fill="none" />
    {/* Bath */}
    <rect x="450" y="320" width="60" height="90" fill="none" />
    <ellipse cx="480" cy="345" rx="18" ry="10" fill="none" />
    <circle cx="480" cy="385" r="10" fill="none" />
    {/* Kitchen counter L */}
    <path d="M 540 320 L 540 410 L 640 410 L 640 380 L 580 380 L 580 320 Z" fill="none" opacity="0.6" />
    <circle cx="560" cy="395" r="6" fill="none" />
    <circle cx="600" cy="395" r="6" fill="none" />

    {/* Hatching on bath area */}
    <g stroke="currentColor" strokeWidth="0.5" opacity="0.35">
      {Array.from({ length: 8 }, (_, i) => (
        <line key={`hero-1-${i}`} x1={452 + i * 7} y1={322} x2={452 + i * 7 - 18} y2={342} />
      ))}
    </g>

    {/* Dimension chains */}
    <DimH x1={120} x2={320} y={102} label="5.20m" />
    <DimH x1={320} x2={520} y={102} label="5.20m" />
    <DimH x1={520} x2={660} y={102} label="3.60m" />
    <DimH x1={120} x2={660} y={86} label="OVERALL 14.00m" />
    <DimV y1={120} y2={280} x={92} label="4.00m" />
    <DimV y1={280} y2={440} x={92} label="4.00m" />

    {/* Room labels */}
    <text x="190" y="170" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">LIVING · 26.0 m²</text>
    <text x="370" y="170" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">DINING</text>
    <text x="560" y="170" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">KITCHEN</text>
    <text x="190" y="340" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">FAMILY</text>
    <text x="445" y="330" fontSize="9" fill="currentColor" stroke="none" opacity="0.65" letterSpacing="1.2">BATH</text>

    {/* Section markers A-A */}
    <g opacity="0.75">
      <line x1="40" y1="240" x2="760" y2="240" strokeDasharray="20 3 3 3" opacity="0.5" />
      <circle cx="50" cy="240" r="11" fill="none" strokeWidth="1.6" />
      <text x="50" y="244" fontSize="11" fill="currentColor" stroke="none" textAnchor="middle">A</text>
      <polygon points="60,240 70,236 70,244" fill="currentColor" />
      <circle cx="750" cy="240" r="11" fill="none" strokeWidth="1.6" />
      <text x="750" y="244" fontSize="11" fill="currentColor" stroke="none" textAnchor="middle">A</text>
      <polygon points="740,240 730,236 730,244" fill="currentColor" />
    </g>

    {/* Detail bubble */}
    <circle cx="220" cy="350" r="40" fill="none" strokeDasharray="3 3" opacity="0.55" />
    <line x1="258" y1="345" x2="320" y2="500" strokeDasharray="3 3" opacity="0.55" />
    <text x="324" y="498" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">DETAIL 3 / A-401</text>

    <NorthArrow x={720} y={70} />
    <ScaleBar x={50} y={555} />
    <TitleBlock project="RESIDENTIAL · TYPE-C" sheet="A-101" scale="1:50" />
  </g>
);

const VariantModeling = () => (
  // Rich isometric of multi-storey building with floors, columns, mullions
  <g>
    <Grid />
    <g transform="translate(400 330)">
      {/* base footprint */}
      <polygon points="-200,40 0,-60 200,40 0,140" fill="none" strokeWidth="2.5" />
      {/* 6 storeys */}
      {[1, 2, 3, 4, 5, 6].map(i => {
        const z = -i * 50;
        const w = 200 - i * 4;
        const h = 100 - i * 2;
        return (
          <g key={`hero-2-${i}`}>
            <polygon points={`${-w},${40 + z} 0,${-h / 2 + z} ${w},${40 + z} 0,${h + z + 40}`} fill="none" />
            {/* floor slabs */}
            <polygon points={`${-w},${42 + z} 0,${-h / 2 + 2 + z} ${w},${42 + z} 0,${h + z + 42}`} fill="none" opacity="0.25" />
          </g>
        );
      })}
      {/* corner columns extended */}
      <line x1="-200" y1="40" x2="-176" y2="-260" strokeWidth="1.6" />
      <line x1="200" y1="40" x2="176" y2="-260" strokeWidth="1.6" />
      <line x1="0" y1="-60" x2="0" y2="-320" strokeWidth="1.6" />
      <line x1="0" y1="140" x2="0" y2="-130" strokeWidth="0.9" opacity="0.55" />

      {/* mullions / vertical fins on left facade */}
      {Array.from({ length: 6 }, (_, i) => {
        const t = (i + 1) / 7;
        const x = -200 + (200) * t;
        const y = 40 - 50 * t;
        return <line key={`ml${i}`} x1={x} y1={y} x2={x + 24} y2={y - 300} opacity="0.45" />;
      })}
      {/* mullions on right facade */}
      {Array.from({ length: 6 }, (_, i) => {
        const t = (i + 1) / 7;
        const x = 200 - (200) * t;
        const y = 40 - 50 * t;
        return <line key={`mr${i}`} x1={x} y1={y} x2={x - 24} y2={y - 300} opacity="0.45" />;
      })}

      {/* floor lines visible on left face */}
      {[1, 2, 3, 4, 5].map(i => {
        const z = -i * 50;
        return <line key={`flz${i}`} x1="-200" y1={40 + z} x2="0" y2={140 + z} opacity="0.5" />;
      })}
      {[1, 2, 3, 4, 5].map(i => {
        const z = -i * 50;
        return <line key={`flz2${i}`} x1="200" y1={40 + z} x2="0" y2={140 + z} opacity="0.5" />;
      })}

      {/* Rooftop helipad */}
      <g transform="translate(0 -300)">
        <ellipse rx="50" ry="20" fill="none" />
        <text x="-5" y="5" fontSize="14" fill="currentColor" stroke="none" opacity="0.65">H</text>
      </g>
      {/* mast */}
      <line x1="0" y1="-300" x2="0" y2="-340" />
      <circle cx="0" cy="-343" r="3" fill="currentColor" />

      {/* vertex dots */}
      {[[-200, 40], [200, 40], [0, -60], [0, 140], [-176, -260], [176, -260], [0, -320], [0, -130]].map(([x, y], i) => (
        <circle key={`hero-3-${i}`} cx={x} cy={y} r="2.5" fill="currentColor" stroke="none" opacity="0.85" />
      ))}
    </g>

    {/* Coordinate axes */}
    <g transform="translate(80 480)">
      <line x1="0" y1="0" x2="70" y2="-35" />
      <Arrow x={70} y={-35} angle={-26} />
      <line x1="0" y1="0" x2="70" y2="35" />
      <Arrow x={70} y={35} angle={26} />
      <line x1="0" y1="0" x2="0" y2="-70" />
      <Arrow x={0} y={-70} angle={-90} />
      <text x="75" y="-35" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">X</text>
      <text x="75" y="42" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">Y</text>
      <text x="-12" y="-72" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">Z</text>
    </g>

    {/* Annotation labels */}
    <line x1="600" y1="100" x2="540" y2="40" strokeDasharray="3 3" opacity="0.6" />
    <text x="600" y="96" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">CURTAIN-WALL · LOD 350</text>
    <line x1="610" y1="200" x2="540" y2="180" strokeDasharray="3 3" opacity="0.6" />
    <text x="610" y="206" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">STEEL FRAME · IFC 4</text>
    <line x1="100" y1="300" x2="250" y2="320" strokeDasharray="3 3" opacity="0.6" />
    <text x="40" y="298" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">RC CORE · CONCRETE</text>
    <line x1="610" y1="380" x2="540" y2="360" strokeDasharray="3 3" opacity="0.6" />
    <text x="610" y="386" fontSize="10" fill="currentColor" stroke="none" opacity="0.75">FOUNDATION SLAB</text>

    <TitleBlock project="BIM · COMMERCIAL TOWER" sheet="M-301" scale="1:200" rev="D" />
  </g>
);

const VariantDesign = () => (
  // Architectural plan with extensive annotations + furniture symbols
  <g>
    <Grid />
    {/* shell */}
    <rect x="120" y="120" width="560" height="360" fill="none" strokeWidth="3" />
    <rect x="128" y="128" width="544" height="344" fill="none" />
    {/* zones */}
    <line x1="380" y1="120" x2="380" y2="300" strokeWidth="2" />
    <line x1="380" y1="300" x2="680" y2="300" strokeWidth="2" />
    <line x1="240" y1="300" x2="380" y2="300" strokeWidth="2" />
    <line x1="240" y1="300" x2="240" y2="480" strokeWidth="2" />
    <line x1="500" y1="300" x2="500" y2="480" strokeWidth="2" />

    {/* Doors */}
    <path d="M 240 320 A 22 22 0 0 1 218 298" fill="none" />
    <path d="M 380 240 A 30 30 0 0 1 410 270" fill="none" />
    <path d="M 500 380 A 30 30 0 0 1 530 410" fill="none" />
    <path d="M 380 360 A 20 20 0 0 1 400 380" fill="none" />

    {/* Windows */}
    <line x1="180" y1="120" x2="280" y2="120" strokeWidth="4" opacity="0.4" />
    <line x1="180" y1="120" x2="280" y2="120" strokeDasharray="3 4" />
    <line x1="500" y1="480" x2="600" y2="480" strokeWidth="4" opacity="0.4" />
    <line x1="500" y1="480" x2="600" y2="480" strokeDasharray="3 4" />
    <line x1="680" y1="180" x2="680" y2="260" strokeWidth="4" opacity="0.4" />
    <line x1="680" y1="180" x2="680" y2="260" strokeDasharray="3 4" />
    <line x1="120" y1="380" x2="120" y2="450" strokeWidth="4" opacity="0.4" />
    <line x1="120" y1="380" x2="120" y2="450" strokeDasharray="3 4" />

    {/* Furniture */}
    <rect x="150" y="380" width="90" height="40" fill="none" />
    <rect x="160" y="385" width="70" height="30" fill="none" opacity="0.5" />
    <rect x="160" y="425" width="20" height="20" fill="none" />
    <rect x="200" y="425" width="20" height="20" fill="none" />
    <ellipse cx="500" cy="200" rx="50" ry="30" fill="none" />
    <line x1="500" y1="170" x2="500" y2="230" opacity="0.4" />
    {[440, 470, 500, 530, 560].map(x => <line key={x} x1={x} y1="195" x2={x} y2="205" opacity="0.4" />)}
    {/* Bedroom: bed + nightstands */}
    <rect x="430" y="370" width="80" height="100" fill="none" />
    <rect x="438" y="380" width="64" height="20" fill="none" opacity="0.5" />
    <rect x="420" y="370" width="10" height="20" fill="none" />
    <rect x="510" y="370" width="10" height="20" fill="none" />
    {/* Bath: tub + sink + toilet */}
    <rect x="265" y="360" width="100" height="50" fill="none" />
    <rect x="270" y="365" width="90" height="40" fill="none" opacity="0.4" />
    <circle cx="290" cy="440" r="12" fill="none" />
    <ellipse cx="340" cy="440" rx="14" ry="10" fill="none" />
    {/* Kitchen counter */}
    <path d="M 550 380 L 550 480 L 660 480 L 660 440 L 600 440 L 600 380 Z" fill="none" opacity="0.6" />
    <circle cx="570" cy="455" r="6" fill="none" />
    <circle cx="595" cy="455" r="6" fill="none" />
    <circle cx="615" cy="463" r="8" fill="none" />

    {/* Stairs */}
    <g>
      <rect x="640" y="160" width="40" height="120" fill="none" />
      {Array.from({ length: 8 }, (_, i) => (
        <line key={`st${i}`} x1="640" y1={170 + i * 14} x2="680" y2={170 + i * 14} opacity="0.5" />
      ))}
      <line x1="640" y1="220" x2="680" y2="220" strokeWidth="1.5" />
      <line x1="640" y1="220" x2="680" y2="220" strokeDasharray="6 4" />
      <text x="652" y="200" fontSize="9" fill="currentColor" stroke="none" opacity="0.7">UP</text>
      <polygon points="660,210 666,200 654,200" fill="currentColor" />
    </g>

    {/* Hatching outside (paving) */}
    <g stroke="currentColor" strokeWidth="0.4" opacity="0.25">
      {Array.from({ length: 30 }, (_, i) => (
        <line key={`pav${i}`} x1={80 + i * 22} y1={92} x2={68 + i * 22} y2={108} />
      ))}
    </g>

    {/* Dim chains */}
    <DimH x1={120} x2={240} y={100} label="3.0m" />
    <DimH x1={240} x2={380} y={100} label="3.5m" />
    <DimH x1={380} x2={500} y={100} label="3.0m" />
    <DimH x1={500} x2={680} y={100} label="4.5m" />
    <DimH x1={120} x2={680} y={82} label="OVERALL 14.0m" />
    <DimV y1={120} y2={300} x={92} label="4.5m" />
    <DimV y1={300} y2={480} x={92} label="4.5m" />

    {/* Room labels with area */}
    <text x="180" y="220" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">LIVING · 28 m²</text>
    <text x="430" y="180" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">DINING · 14 m²</text>
    <text x="565" y="180" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">FOYER</text>
    <text x="180" y="430" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">STUDY</text>
    <text x="285" y="395" fontSize="9" fill="currentColor" stroke="none" opacity="0.65">BATH</text>
    <text x="430" y="430" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">BEDROOM</text>
    <text x="565" y="420" fontSize="10" fill="currentColor" stroke="none" opacity="0.75" letterSpacing="1.5">KITCHEN</text>

    {/* Section A-A */}
    <line x1="40" y1="320" x2="760" y2="320" strokeDasharray="20 3 3 3" opacity="0.45" />
    <circle cx="50" cy="320" r="11" fill="none" />
    <text x="50" y="324" fontSize="11" fill="currentColor" stroke="none" textAnchor="middle">B</text>

    <NorthArrow x={720} y={70} />
    <ScaleBar x={50} y={555} />
    <TitleBlock project="MULTI-FAMILY · TYPE-A" sheet="A-103" scale="1:50" rev="B" />
  </g>
);

const VariantSurveying = () => (
  <g>
    <Grid />
    {/* Boundary segments as named paths so we can render bearing/distance labels parallel to each line */}
    <defs>
      {/* Each path drawn slightly above the boundary line so text sits on top of it */}
      <path id="surv-seg-1" d="M 120 200 L 320 140" />
      <path id="surv-seg-2" d="M 320 140 L 600 200" />
      <path id="surv-seg-3" d="M 600 200 L 660 380" />
      <path id="surv-seg-4" d="M 660 380 L 480 500" />
      <path id="surv-seg-5" d="M 480 500 L 200 460" />
      <path id="surv-seg-6" d="M 200 460 L 140 340" />
      <path id="surv-seg-7" d="M 140 340 L 120 200" />
    </defs>

    {/* The actual boundary polygon — drawn-on animation applies here */}
    <polygon points="120,200 320,140 600,200 660,380 480,500 200,460 140,340" fill="none" strokeWidth="2.8" />

    {/* Bearings & distances — labels follow the line direction via textPath */}
    <g fill="currentColor" stroke="none" opacity="0.9" fontSize="9" letterSpacing="0.5">
      <text dy="-5"><textPath href="#surv-seg-1" startOffset="50%" textAnchor="middle">N 48°24&apos;04&quot; E · 169.77&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-2" startOffset="50%" textAnchor="middle">S 42°12&apos;59&quot; E · 213.48&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-3" startOffset="50%" textAnchor="middle">S 88°34&apos;10&quot; W · 162.16&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-4" startOffset="50%" textAnchor="middle">S 41°30&apos;35&quot; W · 93.18&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-5" startOffset="50%" textAnchor="middle">N 88°34&apos;10&quot; W · 280.12&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-6" startOffset="50%" textAnchor="middle">N 62°15&apos;22&quot; W · 132.08&apos;</textPath></text>
      <text dy="-5"><textPath href="#surv-seg-7" startOffset="50%" textAnchor="middle">N 04°47&apos;00&quot; E · 142.50&apos;</textPath></text>
    </g>

    {/* Setback offset (dashed parallel) inside boundary */}
    <polygon points="170,225 320,175 580,225 630,365 470,470 220,440 175,335" fill="none" strokeDasharray="3 4" opacity="0.35" />

    {/* Corner monuments with rich annotations */}
    {[
      [120, 200, '1/2" REBAR', 'PT-1'], [320, 140, 'IP FOUND', 'PT-2'], [600, 200, 'IP FOUND', 'PT-3'],
      [660, 380, '1/2" REBAR', 'PT-4'], [480, 500, 'IP FOUND', 'PT-5'], [200, 460, '1/2" REBAR', 'PT-6'], [140, 340, 'CONC. MON', 'PT-7']
    ].map(([cx, cy, label, pt], i) => (
      <g key={`hero-4-${i}`}>
        <circle cx={cx} cy={cy} r="7" fill="none" strokeWidth="1.4" />
        <circle cx={cx} cy={cy} r="2.2" fill="currentColor" />
        <text x={cx + 12} y={cy - 10} fontSize="8" fill="currentColor" stroke="none" opacity="0.85">{pt}</text>
        <text x={cx + 12} y={cy - 1} fontSize="7" fill="currentColor" stroke="none" opacity="0.55">{label}</text>
      </g>
    ))}

    {/* Contour lines inside property — wavy elevation contours */}
    {[0, 1, 2, 3, 4].map(i => (
      <path key={`hero-5-${i}`} d={`M ${200 + i * 10} ${290 + i * 10} Q ${340 + i * 5} ${260 + i * 8} ${470 + i * 5} ${300 + i * 6} Q ${550 + i * 3} ${360 + i * 4} ${500 + i * 4} ${420 + i * 4}`} fill="none" opacity="0.4" />
    ))}
    {/* Contour elevation labels — placed in gaps */}
    <text x="345" y="263" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" transform="rotate(-12 345 263)">847.80</text>
    <text x="395" y="295" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" transform="rotate(-8 395 295)">848.50</text>
    <text x="430" y="335" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" transform="rotate(-4 430 335)">849.20</text>

    {/* Building footprint with hatching */}
    <rect x="340" y="280" width="120" height="90" fill="none" strokeWidth="1.5" />
    <text x="360" y="318" fontSize="11" fill="currentColor" stroke="none" opacity="0.8" fontWeight="bold">#1875</text>
    <text x="355" y="334" fontSize="9" fill="currentColor" stroke="none" opacity="0.65">TWO STORY</text>
    <text x="350" y="347" fontSize="9" fill="currentColor" stroke="none" opacity="0.55">BRICK &amp; FRAME</text>
    <text x="368" y="361" fontSize="8" fill="currentColor" stroke="none" opacity="0.5">FF 850.25</text>
    <g stroke="currentColor" strokeWidth="0.4" opacity="0.32">
      {Array.from({ length: 18 }, (_, i) => (
        <line key={`hero-6-${i}`} x1={340 + i * 7} y1={280} x2={340 + i * 7 - 30} y2={310} />
      ))}
    </g>
    {/* Driveway lines from building to road */}
    <path d="M 400 370 L 400 460 L 480 500" fill="none" strokeDasharray="6 3" opacity="0.45" />

    {/* Easements (dashed lines) */}
    <line x1="135" y1="232" x2="640" y2="225" strokeDasharray="14 6" opacity="0.55" />
    <text x="305" y="218" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" letterSpacing="1">10' DRAINAGE EASEMENT</text>
    <line x1="220" y1="430" x2="640" y2="380" strokeDasharray="6 5" opacity="0.5" />
    <text x="370" y="420" fontSize="8" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1" transform="rotate(-7 370 420)">25' STATE BUFFER</text>

    {/* Stream / creek (dash-dot) along right edge */}
    <path d="M 600 200 Q 580 280 600 360" fill="none" strokeDasharray="14 4 3 4" opacity="0.65" />
    <text x="608" y="280" fontSize="8" fill="currentColor" stroke="none" opacity="0.6" letterSpacing="1" transform="rotate(90 608 280)">C/L OF CREEK</text>

    {/* Trees — random natural feature dots */}
    {[[210, 250], [240, 280], [260, 250], [560, 260], [580, 310], [555, 350], [220, 420], [260, 410], [560, 460], [600, 430]].map(([x, y], i) => (
      <g key={`tr${i}`} opacity="0.65">
        <circle cx={x} cy={y} r="5" fill="none" />
        <line x1={x} y1={y + 5} x2={x} y2={y + 10} />
      </g>
    ))}

    {/* Road right-of-way at bottom */}
    <line x1="60" y1="540" x2="760" y2="540" strokeWidth="1.5" opacity="0.55" />
    <line x1="60" y1="530" x2="760" y2="530" strokeDasharray="14 8" opacity="0.5" />
    <text x="60" y="525" fontSize="8" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1">ACUBA LANE · 60' R/W</text>

    {/* Lot area callout — bold central label */}
    <g transform="translate(380 220)">
      <rect x="-50" y="-12" width="100" height="24" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.55" />
      <text x="0" y="4" fontSize="11" fill="currentColor" stroke="none" textAnchor="middle" fontWeight="bold" letterSpacing="1">LOT 8 · 0.93 AC</text>
    </g>

    {/* Found vs Set monument legend */}
    <g transform="translate(60 425)" opacity="0.78">
      <rect x="-6" y="-6" width="160" height="56" fill="none" strokeOpacity="0.4" />
      <text x="-2" y="3" fontSize="8" fill="currentColor" stroke="none" letterSpacing="1">LEGEND</text>
      <circle cx="4" cy="18" r="5" fill="none" /><circle cx="4" cy="18" r="1.4" fill="currentColor" />
      <text x="14" y="21" fontSize="8" fill="currentColor" stroke="none">REBAR FOUND</text>
      <g transform="translate(0 16)">
        <rect x="-1" y="11" width="10" height="10" fill="none" />
        <text x="14" y="20" fontSize="8" fill="currentColor" stroke="none">MON. SET</text>
      </g>
    </g>

    {/* Title strip */}
    <text x="60" y="68" fontSize="11" fill="currentColor" stroke="none" opacity="0.85" letterSpacing="2" fontWeight="bold">BOUNDARY SURVEY · ALTA / NSPS</text>
    <text x="60" y="86" fontSize="9" fill="currentColor" stroke="none" opacity="0.6" letterSpacing="1.5">SURVEYED 2026-Q1 · CLOSURE 1:20,000 · ACREAGE 0.93</text>

    <NorthArrow x={720} y={80} />
    <ScaleBar x={50} y={555} label="0  20'  40'  60'" />
    <TitleBlock project="ALTA SURVEY · LOT 8" sheet="S-001" scale="1=20'" rev="C" />
  </g>
);

const VariantEngineering = () => (
  <g>
    <Grid />
    {/* Major equipment */}
    {/* Vertical vessel V-101 */}
    <rect x="120" y="170" width="120" height="230" rx="20" fill="none" strokeWidth="2.5" />
    <line x1="120" y1="210" x2="240" y2="210" />
    <line x1="120" y1="360" x2="240" y2="360" />
    {/* internals */}
    <line x1="155" y1="210" x2="155" y2="360" opacity="0.5" />
    <line x1="180" y1="240" x2="200" y2="240" opacity="0.55" />
    <line x1="180" y1="280" x2="200" y2="280" opacity="0.55" />
    <line x1="180" y1="320" x2="200" y2="320" opacity="0.55" />
    <text x="135" y="160" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">V-101</text>
    <text x="130" y="500" fontSize="8" fill="currentColor" stroke="none" opacity="0.55">SEP. DRUM</text>
    <text x="130" y="512" fontSize="8" fill="currentColor" stroke="none" opacity="0.55">2.5m × 4m</text>

    {/* Pump P-205 */}
    <circle cx="380" cy="430" r="38" fill="none" />
    <polygon points="350,430 410,410 410,450" fill="none" />
    <line x1="345" y1="395" x2="345" y2="465" />
    <line x1="345" y1="395" x2="415" y2="395" />
    <line x1="345" y1="465" x2="415" y2="465" />
    <text x="362" y="495" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">P-205A</text>
    <text x="350" y="510" fontSize="8" fill="currentColor" stroke="none" opacity="0.55">75 kW</text>

    {/* Heat exchanger E-301 */}
    <rect x="500" y="170" width="200" height="70" rx="30" fill="none" strokeWidth="2.5" />
    {Array.from({ length: 7 }, (_, i) => (
      <line key={`hero-7-${i}`} x1={510 + i * 28} y1={180} x2={510 + i * 28} y2={230} opacity="0.55" />
    ))}
    <line x1="514" y1="195" x2="694" y2="195" opacity="0.4" />
    <line x1="514" y1="215" x2="694" y2="215" opacity="0.4" />
    <text x="565" y="160" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">E-301 · SHELL & TUBE</text>
    <text x="565" y="262" fontSize="8" fill="currentColor" stroke="none" opacity="0.55">DUTY 1.8 MW</text>

    {/* Reactor R-102 (separate) */}
    <rect x="520" y="320" width="80" height="160" rx="40" fill="none" strokeWidth="2.5" />
    <line x1="520" y1="380" x2="600" y2="380" opacity="0.5" />
    <circle cx="560" cy="430" r="14" fill="none" opacity="0.7" />
    <text x="525" y="510" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">R-102</text>

    {/* Piping network with arrows */}
    <path d="M 240 290 H 320 V 430 H 342" fill="none" strokeWidth="2" />
    <Arrow x={325} y={290} angle={0} />
    <path d="M 418 430 H 470 V 210 H 500" fill="none" strokeWidth="2" />
    <Arrow x={500} y={210} angle={0} />
    <path d="M 700 210 H 740 V 380 H 600" fill="none" strokeWidth="2" />
    <Arrow x={600} y={380} angle={180} />
    <path d="M 540 480 V 520 H 100 V 400" fill="none" strokeWidth="1.6" strokeDasharray="6 4" opacity="0.65" />

    {/* Valves */}
    {[[280, 290], [500, 380], [470, 290], [610, 380]].map(([x, y], i) => (
      <polygon key={`v${i}`} points={`${x - 10},${y - 10} ${x + 10},${y + 10} ${x + 10},${y - 10} ${x - 10},${y + 10}`} fill="none" />
    ))}

    {/* Instrument circles (PI / TI / FI) */}
    {[
      [180, 130, 'PI', '101'],
      [380, 130, 'TI', '205'],
      [600, 130, 'FI', '301'],
      [720, 350, 'LT', '102']
    ].map(([cx, cy, kind, tag], i) => (
      <g key={`ins${i}`}>
        <circle cx={cx} cy={cy} r="20" fill="none" />
        <line x1={cx - 20} y1={cy} x2={cx + 20} y2={cy} />
        <text x={cx} y={cy - 3} fontSize="9" fill="currentColor" stroke="none" textAnchor="middle" opacity="0.85">{kind}</text>
        <text x={cx} y={cy + 12} fontSize="9" fill="currentColor" stroke="none" textAnchor="middle" opacity="0.75">{tag}</text>
        <line x1={cx} y1={cy + 20} x2={cx} y2={cy + 50} strokeDasharray="3 3" opacity="0.55" />
      </g>
    ))}

    {/* Control signal lines (dashed) */}
    <path d="M 180 150 H 250 V 220" fill="none" strokeDasharray="3 3" opacity="0.6" />
    <path d="M 380 150 V 200" fill="none" strokeDasharray="3 3" opacity="0.6" />

    {/* Flow callouts */}
    <text x="305" y="305" fontSize="8" fill="currentColor" stroke="none" opacity="0.6">120 m³/h · 8 barg</text>
    <text x="485" y="225" fontSize="8" fill="currentColor" stroke="none" opacity="0.6">160 °C · LIQ</text>

    {/* Title */}
    <text x="60" y="70" fontSize="11" fill="currentColor" stroke="none" opacity="0.78" letterSpacing="2">P&amp;ID · UNIT 100 · SEPARATION</text>
    <text x="60" y="90" fontSize="9" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">ISO 14617 SYMBOLS · REV C · 2026-Q1</text>

    <TitleBlock project="REFINERY UNIT 100" sheet="P-101" scale="N.T.S." rev="C" />
  </g>
);

const VariantDevelopment = () => (
  <g>
    <Grid />
    {/* Outer site boundary (irregular) */}
    <polygon points="80,140 720,120 740,500 100,520" fill="none" strokeWidth="2.5" />

    {/* Road network */}
    <line x1="60" y1="320" x2="760" y2="320" strokeWidth="2" />
    <line x1="60" y1="340" x2="760" y2="340" strokeWidth="2" />
    <line x1="60" y1="330" x2="760" y2="330" strokeDasharray="10 12" opacity="0.55" />

    <line x1="400" y1="140" x2="400" y2="320" strokeWidth="2" />
    <line x1="420" y1="140" x2="420" y2="320" strokeWidth="2" />
    <line x1="410" y1="140" x2="410" y2="320" strokeDasharray="10 12" opacity="0.55" />

    <line x1="200" y1="340" x2="200" y2="520" strokeWidth="2" />
    <line x1="220" y1="340" x2="220" y2="520" strokeWidth="2" />
    <line x1="210" y1="340" x2="210" y2="520" strokeDasharray="10 12" opacity="0.45" />

    <line x1="580" y1="340" x2="580" y2="520" strokeWidth="2" />
    <line x1="600" y1="340" x2="600" y2="520" strokeWidth="2" />

    {/* Lots upper-left */}
    {[0, 1, 2, 3].map(i => (
      <g key={`ul${i}`}>
        <rect x={100 + i * 70} y="160" width="60" height="140" fill="none" />
        <text x={108 + i * 70} y="180" fontSize="9" fill="currentColor" stroke="none" opacity="0.75">L-{i + 1}</text>
        <text x={108 + i * 70} y="193" fontSize="7" fill="currentColor" stroke="none" opacity="0.5">0.18 ac</text>
        {/* footprint */}
        <rect x={112 + i * 70} y="220" width="36" height="40" fill="none" opacity="0.55" />
      </g>
    ))}

    {/* Lots upper-right */}
    {[0, 1, 2, 3, 4].map(i => (
      <g key={`ur${i}`}>
        <rect x={440 + i * 60} y="160" width="50" height="140" fill="none" />
        <text x={446 + i * 60} y="180" fontSize="9" fill="currentColor" stroke="none" opacity="0.75">L-{i + 5}</text>
        <text x={446 + i * 60} y="193" fontSize="7" fill="currentColor" stroke="none" opacity="0.5">0.15 ac</text>
        <rect x={450 + i * 60} y="220" width="30" height="40" fill="none" opacity="0.55" />
      </g>
    ))}

    {/* Lots lower-left */}
    {[0, 1].map(i => (
      <g key={`lll${i}`}>
        <rect x={100 + i * 90} y="360" width="80" height="140" fill="none" />
        <text x={108 + i * 90} y="380" fontSize="9" fill="currentColor" stroke="none" opacity="0.75">L-{i + 10}</text>
        <rect x={115 + i * 90} y="410" width="50" height="50" fill="none" opacity="0.55" />
      </g>
    ))}
    {/* Lots lower-center (with park gap) */}
    {[0, 1, 2].map(i => (
      <g key={`llc${i}`}>
        <rect x={240 + i * 90} y="360" width="80" height="140" fill="none" />
        <text x={248 + i * 90} y="380" fontSize="9" fill="currentColor" stroke="none" opacity="0.75">L-{i + 12}</text>
        <rect x={255 + i * 90} y="410" width="50" height="50" fill="none" opacity="0.55" />
      </g>
    ))}
    {/* Lots lower-right */}
    {[0, 1, 2].map(i => (
      <g key={`llr${i}`}>
        <rect x={620 + i * 40} y="360" width="36" height="140" fill="none" />
        <text x={626 + i * 40} y="380" fontSize="9" fill="currentColor" stroke="none" opacity="0.75">L-{i + 15}</text>
      </g>
    ))}

    {/* Detention pond (irregular) */}
    <path d="M 510 380 Q 570 360 610 400 Q 630 440 590 480 Q 540 500 500 470 Q 480 430 510 380 Z" fill="none" strokeWidth="1.4" />
    <g stroke="currentColor" strokeWidth="0.4" opacity="0.3">
      {Array.from({ length: 6 }, (_, i) => (
        <line key={`hero-8-${i}`} x1={515 + i * 14} y1={395} x2={500 + i * 14} y2={415} />
      ))}
    </g>
    <text x="525" y="445" fontSize="9" fill="currentColor" stroke="none" opacity="0.65">POND</text>
    <text x="510" y="458" fontSize="7" fill="currentColor" stroke="none" opacity="0.5">12,400 cf</text>

    {/* Cul-de-sac with center turning circle */}
    <circle cx="700" cy="220" r="42" fill="none" strokeWidth="2" />
    <circle cx="700" cy="220" r="28" fill="none" strokeDasharray="4 4" opacity="0.5" />
    <line x1="658" y1="220" x2="420" y2="220" strokeDasharray="8 8" opacity="0.45" />
    <text x="688" y="220" fontSize="8" fill="currentColor" stroke="none" opacity="0.55" textAnchor="middle">R 42'</text>

    {/* Street trees */}
    {[60, 130, 280, 350, 430, 500, 570, 640, 720].map((x, i) => (
      <circle key={`tr${i}`} cx={x} cy={355} r="4" fill="none" opacity="0.55" />
    ))}
    {[60, 130, 280, 350, 430, 500, 570, 640, 720].map((x, i) => (
      <circle key={`tr2${i}`} cx={x} cy={305} r="4" fill="none" opacity="0.55" />
    ))}

    {/* Utility line (sewer) */}
    <path d="M 80 330 L 760 330" fill="none" strokeDasharray="3 4 12 4" opacity="0" />
    <path d="M 80 360 L 760 360" fill="none" strokeDasharray="14 4 3 4" opacity="0.45" />
    <text x="690" y="357" fontSize="7" fill="currentColor" stroke="none" opacity="0.55">SAN. SEW.</text>
    <path d="M 80 304 L 760 304" fill="none" strokeDasharray="6 3 2 3" opacity="0.45" />
    <text x="690" y="301" fontSize="7" fill="currentColor" stroke="none" opacity="0.55">WATER MAIN</text>

    {/* Manholes */}
    {[150, 320, 500, 680].map((x, i) => (
      <g key={`mh${i}`}>
        <circle cx={x} cy={360} r="4" fill="none" strokeWidth="1.2" />
        <circle cx={x} cy={360} r="1.5" fill="currentColor" />
      </g>
    ))}

    {/* Phase labels */}
    <text x="120" y="155" fontSize="8" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">PHASE 1</text>
    <text x="450" y="155" fontSize="8" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">PHASE 2</text>

    {/* Title */}
    <text x="60" y="70" fontSize="11" fill="currentColor" stroke="none" opacity="0.78" letterSpacing="2">MASTER SITE PLAN · PHASE 1+2</text>
    <text x="60" y="90" fontSize="9" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">17 LOTS · 4.2 ACRES · ZONE R-3 · DENSITY 4.1/ac</text>

    <NorthArrow x={720} y={80} />
    <ScaleBar x={50} y={555} label="0  50'  100'  150'" />
    <TitleBlock project="OAKWOOD SUBDIVISION" sheet="C-001" scale="1:100" rev="C" />
  </g>
);

const VariantArchitecting = () => (
  <g>
    <Grid rows={[120, 280, 440]} />
    {/* Ground line */}
    <line x1="60" y1="500" x2="760" y2="500" strokeWidth="2.5" />
    <g stroke="currentColor" strokeWidth="0.4" opacity="0.3">
      {Array.from({ length: 60 }, (_, i) => (
        <line key={`hero-9-${i}`} x1={62 + i * 12} y1={502} x2={56 + i * 12} y2={514} />
      ))}
    </g>

    {/* Main facade */}
    <rect x="170" y="170" width="460" height="330" fill="none" strokeWidth="2.5" />
    <polygon points="170,170 400,90 630,170" fill="none" strokeWidth="2.5" />
    <line x1="400" y1="90" x2="400" y2="170" />
    {/* roof tile pattern (subtle) */}
    <g stroke="currentColor" strokeWidth="0.4" opacity="0.4">
      {Array.from({ length: 10 }, (_, i) => (
        <line key={`hero-10-${i}`} x1={170 + i * 23} y1={170 - i * 4} x2={170 + i * 23 + 50} y2={170 - i * 4 - 17} />
      ))}
    </g>

    {/* Floor lines */}
    <line x1="170" y1="260" x2="630" y2="260" />
    <line x1="170" y1="350" x2="630" y2="350" />
    <line x1="170" y1="440" x2="630" y2="440" />

    {/* Window grid floor 1 */}
    {[0, 1, 2, 3, 4].map(i => (
      <g key={`w1${i}`}>
        <rect x={195 + i * 90} y="195" width="55" height="50" fill="none" />
        <line x1={222 + i * 90} y1="195" x2={222 + i * 90} y2="245" opacity="0.5" />
        <line x1={195 + i * 90} y1="220" x2={250 + i * 90} y2="220" opacity="0.5" />
      </g>
    ))}
    {/* Floor 2 windows */}
    {[0, 1, 2, 3, 4].map(i => (
      <g key={`w2${i}`}>
        <rect x={195 + i * 90} y="285" width="55" height="50" fill="none" />
        <line x1={222 + i * 90} y1="285" x2={222 + i * 90} y2="335" opacity="0.5" />
      </g>
    ))}
    {/* Floor 3 — French windows + balcony */}
    {[0, 1, 2, 3, 4].map(i => (
      <g key={`w3${i}`}>
        <rect x={195 + i * 90} y="370" width="55" height="65" fill="none" />
        <line x1={222 + i * 90} y1="370" x2={222 + i * 90} y2="435" opacity="0.5" />
      </g>
    ))}
    {/* Continuous balcony rail */}
    <line x1="180" y1="440" x2="620" y2="440" />
    {Array.from({ length: 50 }, (_, i) => (
      <line key={`br${i}`} x1={180 + i * 9} y1={440} x2={180 + i * 9} y2={462} opacity="0.45" />
    ))}

    {/* Entry stairs + door */}
    <rect x="370" y="450" width="60" height="50" fill="none" strokeWidth="2" />
    <line x1="400" y1="450" x2="400" y2="500" />
    <line x1="360" y1="500" x2="360" y2="475" />
    <line x1="350" y1="500" x2="350" y2="485" />
    <line x1="340" y1="500" x2="340" y2="495" />
    <line x1="440" y1="500" x2="440" y2="475" />
    <line x1="450" y1="500" x2="450" y2="485" />
    <line x1="460" y1="500" x2="460" y2="495" />

    {/* Chimney */}
    <rect x="500" y="50" width="22" height="50" fill="none" />
    <line x1="497" y1="50" x2="525" y2="50" strokeWidth="1.6" />

    {/* Trees with foliage detail */}
    <g transform="translate(120 500)">
      <line x1="0" y1="0" x2="0" y2="-30" strokeWidth="1.6" />
      <circle cx="0" cy="-45" r="18" fill="none" />
      <circle cx="-10" cy="-50" r="12" fill="none" opacity="0.55" />
      <circle cx="10" cy="-42" r="10" fill="none" opacity="0.55" />
    </g>
    <g transform="translate(690 500)">
      <line x1="0" y1="0" x2="0" y2="-25" strokeWidth="1.6" />
      <circle cx="0" cy="-38" r="14" fill="none" />
      <circle cx="-8" cy="-43" r="10" fill="none" opacity="0.55" />
    </g>

    {/* People silhouettes for scale */}
    <g transform="translate(80 490)" opacity="0.55">
      <circle cx="0" cy="0" r="3" fill="none" />
      <line x1="0" y1="3" x2="0" y2="14" />
      <line x1="0" y1="6" x2="-4" y2="11" />
      <line x1="0" y1="6" x2="4" y2="11" />
      <line x1="0" y1="14" x2="-3" y2="20" />
      <line x1="0" y1="14" x2="3" y2="20" />
    </g>
    <g transform="translate(95 488)" opacity="0.55">
      <circle cx="0" cy="0" r="2.5" fill="none" />
      <line x1="0" y1="3" x2="0" y2="12" />
      <line x1="0" y1="6" x2="3" y2="11" />
      <line x1="0" y1="12" x2="-3" y2="18" />
      <line x1="0" y1="12" x2="3" y2="18" />
    </g>

    {/* Dim lines bottom */}
    <DimH x1={60} x2={170} y={528} label="3.0m" />
    <DimH x1={170} x2={400} y={528} label="7.3m" />
    <DimH x1={400} x2={630} y={528} label="7.3m" />
    <DimH x1={630} x2={760} y={528} label="3.6m" />
    <DimV y1={500} y2={170} x={150} label="GROUND TO EAVE 11.0m" />
    <DimV y1={170} y2={90} x={150} label="ROOF 2.7m" />
    <DimV y1={500} y2={440} x={650} label="3F 2.0m" />
    <DimV y1={440} y2={350} x={650} label="2F 3.0m" />
    <DimV y1={350} y2={260} x={650} label="1F 3.0m" />
    <DimV y1={260} y2={170} x={650} label="GF 3.0m" />

    {/* Level markers */}
    {[
      [70, 500, '±0.00 GF'],
      [70, 440, '+3.00 1F'],
      [70, 350, '+6.00 2F'],
      [70, 260, '+9.00 3F'],
      [70, 170, '+12.00 EAVE'],
      [70, 90, '+15.00 RIDGE']
    ].map(([x, y, lbl], i) => (
      <g key={`hero-11-${i}`}>
        <polygon points={`${x},${y} ${x + 8},${y - 6} ${x + 8},${y + 6}`} fill="none" strokeWidth="1.4" />
        <text x={x + 12} y={y + 3} fontSize="9" fill="currentColor" stroke="none" opacity="0.75">{lbl}</text>
      </g>
    ))}

    {/* Title */}
    <text x="80" y="60" fontSize="11" fill="currentColor" stroke="none" opacity="0.78" letterSpacing="2">FRONT ELEVATION · NORTH</text>
    <text x="80" y="78" fontSize="9" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">RC FRAME · STUCCO + COMPOSITE CLADDING</text>

    <TitleBlock project="VILLA SERIES · TYPE-N" sheet="A-201" scale="1:100" rev="C" />
  </g>
);

const VariantStructuring = () => (
  <g>
    <Grid cols={[140, 270, 400, 530, 660]} rows={[180, 280, 380, 480]} />
    {/* Columns */}
    {[0, 1, 2, 3, 4].map(i => {
      const x = 140 + i * 130;
      return (
        <g key={`c${i}`}>
          <line x1={x} y1="120" x2={x} y2="500" strokeWidth="4" />
          {/* I-beam end caps */}
          <rect x={x - 8} y="120" width="16" height="3" fill="currentColor" />
          <rect x={x - 8} y="497" width="16" height="3" fill="currentColor" />
          {/* web */}
          <line x1={x - 1} y1="120" x2={x - 1} y2="500" opacity="0.35" />
          <line x1={x + 1} y1="120" x2={x + 1} y2="500" opacity="0.35" />
        </g>
      );
    })}

    {/* Beams (4 floor levels) */}
    {[180, 280, 380, 480].map((y, i) => (
      <g key={`b${i}`}>
        <line x1="140" y1={y} x2="660" y2={y} strokeWidth="3" />
        <rect x="140" y={y - 2} width="520" height="4" fill="none" opacity="0.3" />
        <text x="80" y={y + 4} fontSize="9" fill="currentColor" stroke="none" opacity="0.65">W12×{26 + i * 4}</text>
      </g>
    ))}

    {/* Diagonal bracing in bays */}
    {[0, 1, 2, 3].map(i => {
      const x = 140 + i * 130;
      return (
        <g key={`br${i}`}>
          <line x1={x} y1="180" x2={x + 130} y2="280" opacity="0.6" />
          <line x1={x + 130} y1="180" x2={x} y2="280" opacity="0.6" />
          <line x1={x} y1="380" x2={x + 130} y2="480" opacity="0.5" />
          <line x1={x + 130} y1="380" x2={x} y2="480" opacity="0.5" />
        </g>
      );
    })}

    {/* Connection plates with bolts */}
    {[140, 270, 400, 530, 660].flatMap(x => [180, 280, 380, 480].map(y => (
      <g key={`p${x}-${y}`}>
        <rect x={x - 10} y={y - 5} width="20" height="10" fill="none" opacity="0.75" />
        {/* bolts (4) */}
        {[-5, 5].map(dx => (
          [-2, 2].map(dy => (
            <circle key={`bo${x}-${y}-${dx}-${dy}`} cx={x + dx} cy={y + dy} r="1.2" fill="currentColor" opacity="0.85" />
          ))
        ))}
      </g>
    )))}

    {/* Base plates with anchor bolts */}
    {[0, 1, 2, 3, 4].map(i => {
      const x = 140 + i * 130;
      return (
        <g key={`bp${i}`}>
          <rect x={x - 18} y="500" width="36" height="10" fill="none" strokeWidth="1.5" />
          {[-12, 12].map(dx => (
            [3, 7].map(dy => (
              <circle key={`ab${i}-${dx}-${dy}`} cx={x + dx} cy={500 + dy} r="1.4" fill="currentColor" />
            ))
          ))}
          {/* footing dashed below */}
          <rect x={x - 30} y="515" width="60" height="20" fill="none" strokeDasharray="3 3" opacity="0.5" />
        </g>
      );
    })}

    {/* Detail callout — connection */}
    <circle cx="660" cy="380" r="45" fill="none" strokeDasharray="4 4" opacity="0.6" />
    <line x1="700" y1="375" x2="750" y2="320" strokeDasharray="3 3" opacity="0.6" />
    <text x="685" y="310" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">DETAIL A · S-401</text>

    <circle cx="140" cy="180" r="40" fill="none" strokeDasharray="4 4" opacity="0.55" />
    <line x1="105" y1="200" x2="55" y2="240" strokeDasharray="3 3" opacity="0.6" />
    <text x="20" y="235" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">DETAIL B</text>

    {/* Loads */}
    {[270, 400, 530].map((x, i) => (
      <g key={`load${i}`}>
        <line x1={x - 30} y1="155" x2={x + 30} y2="155" strokeWidth="1.4" />
        {[-25, -15, -5, 5, 15, 25].map(dx => (
          <line key={dx} x1={x + dx} y1="155" x2={x + dx} y2="175" strokeWidth="1.2" />
        ))}
        {[-25, -15, -5, 5, 15, 25].map(dx => (
          <polygon key={`la${dx}`} points={`${x + dx - 3},${172} ${x + dx + 3},${172} ${x + dx},${180}`} fill="currentColor" />
        ))}
      </g>
    ))}
    <text x="350" y="148" fontSize="9" fill="currentColor" stroke="none" opacity="0.75" textAnchor="middle">UDL 18 kN/m</text>

    {/* Title */}
    <text x="80" y="60" fontSize="11" fill="currentColor" stroke="none" opacity="0.78" letterSpacing="2">STRUCTURAL FRAMING PLAN · LEVEL 2</text>
    <text x="80" y="78" fontSize="9" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">W12 BEAMS · HSS 8×8×½ COLUMNS · A992 STEEL</text>

    <TitleBlock project="OFFICE TOWER · BLOCK A" sheet="S-201" scale="1:100" rev="D" />
  </g>
);

const VariantDetailing = () => (
  <g>
    {/* Plan key reference (small) top-left */}
    <g transform="translate(60 70)">
      <rect width="140" height="100" fill="none" />
      <line x1="0" y1="50" x2="140" y2="50" opacity="0.4" />
      <line x1="70" y1="0" x2="70" y2="100" opacity="0.4" />
      <circle cx="48" cy="32" r="11" fill="none" strokeWidth="1.6" />
      <text x="48" y="36" fontSize="10" fill="currentColor" stroke="none" textAnchor="middle" opacity="0.85">A</text>
      <text x="0" y="-6" fontSize="9" fill="currentColor" stroke="none" opacity="0.55">KEY PLAN · S-201</text>
    </g>

    {/* Main detail — beam-to-column moment connection */}
    <g transform="translate(380 320)">
      {/* Column W14 - heavy I-beam profile */}
      <rect x="-32" y="-220" width="64" height="440" fill="none" strokeWidth="3" />
      <line x1="-32" y1="-220" x2="-32" y2="220" opacity="0.4" />
      <line x1="32" y1="-220" x2="32" y2="220" opacity="0.4" />
      {/* flange thickness */}
      <line x1="-32" y1="-210" x2="32" y2="-210" opacity="0.5" />
      <line x1="-32" y1="210" x2="32" y2="210" opacity="0.5" />
      {/* fill hatching */}
      <g stroke="currentColor" strokeWidth="0.4" opacity="0.4">
        {Array.from({ length: 30 }, (_, i) => (
          <line key={`hero-12-${i}`} x1={-30 + i * 4} y1={-208} x2={-30 + i * 4 + 60} y2={-208 + 60} />
        ))}
      </g>
      {/* Beam coming in from right */}
      <rect x="32" y="-32" width="260" height="64" fill="none" strokeWidth="3" />
      <line x1="32" y1="-22" x2="292" y2="-22" opacity="0.5" />
      <line x1="32" y1="22" x2="292" y2="22" opacity="0.5" />
      {/* web stiffeners on column inside */}
      <line x1="-32" y1="-32" x2="32" y2="-32" />
      <line x1="-32" y1="32" x2="32" y2="32" />
      {/* End plate */}
      <rect x="32" y="-44" width="10" height="88" fill="none" strokeWidth="1.5" />
      {/* Bolt pattern (8 bolts) */}
      {[-30, -10, 10, 30].map(dy => (
        [4, 18].map(dx => (
          <g key={`bol${dy}-${dx}`}>
            <circle cx={32 + dx} cy={dy} r="3" fill="none" strokeWidth="1.2" />
            <circle cx={32 + dx} cy={dy} r="1.4" fill="currentColor" />
          </g>
        ))
      ))}
      {/* Weld symbol arrows */}
      <path d="M 32 -32 L 38 -27 L 32 -22 L 38 -17 L 32 -12 L 38 -7 L 32 -2 L 38 3 L 32 8 L 38 13 L 32 18 L 38 23 L 32 28" fill="none" strokeWidth="1.4" />
    </g>

    {/* Centerline */}
    <line x1="380" y1="100" x2="380" y2="540" strokeDasharray="20 3 3 3" opacity="0.4" />

    {/* Dimensions */}
    <DimV y1={288} y2={352} x={440} label="64" />
    <DimV y1={258} y2={382} x={462} label="100 PLT" />
    <DimH x1={380} x2={412} y={460} label="32" />
    <DimH x1={412} x2={422} y={460} label="10" />

    {/* Bolt spacing */}
    <DimV y1={290} y2={310} x={490} label="P 20" />
    <DimV y1={310} y2={330} x={490} label="P 20" />
    <DimV y1={330} y2={350} x={490} label="P 20" />

    {/* Leader-line callouts */}
    <line x1="425" y1="280" x2="600" y2="180" strokeDasharray="3 3" opacity="0.65" />
    <line x1="600" y1="180" x2="640" y2="180" />
    <text x="650" y="183" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">M20 × 70 GR 10.9 BOLTS (8 NO.)</text>

    <line x1="430" y1="240" x2="600" y2="140" strokeDasharray="3 3" opacity="0.65" />
    <line x1="600" y1="140" x2="640" y2="140" />
    <text x="650" y="143" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">END PLATE 220×100×10 PL</text>

    <line x1="345" y1="-80 + 400" x2="180" y2="180" strokeDasharray="3 3" opacity="0.65" />
    <line x1="180" y1="180" x2="140" y2="180" />
    <text x="60" y="183" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">HSS 14×14×½ COL</text>

    <line x1="490" y1="320" x2="640" y2="380" strokeDasharray="3 3" opacity="0.65" />
    <line x1="640" y1="380" x2="680" y2="380" />
    <text x="685" y="383" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">W21×62 BEAM</text>

    <line x1="375" y1="305" x2="200" y2="395" strokeDasharray="3 3" opacity="0.65" />
    <line x1="200" y1="395" x2="160" y2="395" />
    <text x="60" y="398" fontSize="10" fill="currentColor" stroke="none" opacity="0.85">8mm FILLET WELD ALL ROUND</text>

    {/* Weld symbol (AWS) */}
    <g transform="translate(540 240)">
      <line x1="0" y1="0" x2="40" y2="0" />
      <polygon points="0,0 -8,-6 -8,6" fill="currentColor" />
      <polygon points="20,-8 28,0 20,0" fill="none" />
      <text x="22" y="-10" fontSize="9" fill="currentColor" stroke="none">8</text>
    </g>

    {/* Material legend */}
    <g transform="translate(620 470)" opacity="0.75">
      <text fontSize="9" fill="currentColor" stroke="none" letterSpacing="1.5">MATERIAL</text>
      <line x1="0" y1="6" x2="130" y2="6" />
      <text y="22" fontSize="8" fill="currentColor" stroke="none">STEEL A992 / S355</text>
      <text y="36" fontSize="8" fill="currentColor" stroke="none">BOLTS · A325 / 10.9</text>
      <text y="50" fontSize="8" fill="currentColor" stroke="none">WELDS · E70XX</text>
    </g>

    {/* Title block */}
    <text x="220" y="60" fontSize="11" fill="currentColor" stroke="none" opacity="0.78" letterSpacing="2">CONNECTION DETAIL · D-04</text>
    <text x="220" y="78" fontSize="9" fill="currentColor" stroke="none" opacity="0.55" letterSpacing="1.5">MOMENT CONNECTION · BEAM-TO-COLUMN · SCALE 1:5</text>

    {/* Scale ruler */}
    <ScaleBar x={80} y={555} label="0  25  50  75  100mm" />
    <TitleBlock project="OFFICE TOWER · BLOCK A" sheet="S-401" scale="1:5" rev="C" />
  </g>
);

const VARIANTS = {
  DRAFTING: VariantDrafting,
  MODELING: VariantModeling,
  DESIGN: VariantDesign,
  SURVEYING: VariantSurveying,
  ENGINEERING: VariantEngineering,
  DEVELOPMENT: VariantDevelopment,
  ARCHITECTING: VariantArchitecting,
  STRUCTURING: VariantStructuring,
  DETAILING: VariantDetailing
};

const HeroLineArt = ({ word = 'DRAFTING', accent = '#94a3b8', accentAlt = '#f97316' }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const drawables = svg.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse');
    const texts = svg.querySelectorAll('text, textPath');

    drawables.forEach((p, i) => {
      try {
        if (typeof p.getTotalLength !== 'function') return;
        const len = p.getTotalLength();
        if (!len) return;
        p.style.transition = 'none';
        p.style.strokeDasharray = `${len}`;
        p.style.strokeDashoffset = `${len}`;
        // eslint-disable-next-line no-unused-expressions
        p.getBoundingClientRect();
        // Stagger across ~4.5s with slight randomness; longer ease for a more elegant feel
        const delay = i * 14 + ((i * 53) % 320);
        p.style.transition = `stroke-dashoffset 1900ms cubic-bezier(0.65,0,0.35,1) ${delay}ms`;
        p.style.strokeDashoffset = '0';
      } catch (err) {
        // getBoundingClientRect / stroke-dashoffset can fail on non-rendered SVG paths — safe to skip.
        logger.debug('[HeroLineArt] path animation skipped:', err);
      }
    });

    // Fade text in AFTER the lines start drawing, so labels appear on completed lines
    texts.forEach((t, i) => {
      t.style.opacity = '0';
      t.style.transition = 'none';
      // force reflow
      // eslint-disable-next-line no-unused-expressions
      t.getBoundingClientRect();
      const delay = 1400 + i * 18;
      t.style.transition = `opacity 700ms ease-out ${delay}ms`;
      t.style.opacity = '';
    });
  }, [word]);

  const Variant = VARIANTS[word] || VariantDrafting;

  return (
    <div className="relative w-full h-[460px] rounded-lg overflow-hidden border border-gray-700/40 bg-[#0a1020]" data-testid="hero-lineart">
      {/* Soft radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(148,163,184,0.18),transparent_60%),radial-gradient(circle_at_70%_70%,rgba(249,115,22,0.12),transparent_55%)] pointer-events-none"></div>

      {/* Faint blueprint grid (two layers — minor + major) */}
      <svg className="absolute inset-0 w-full h-full opacity-25 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="hla-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.4" />
          </pattern>
          <pattern id="hla-grid-major" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.7" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hla-grid)" />
        <rect width="100%" height="100%" fill="url(#hla-grid-major)" />
      </svg>

      {/* Animated drawing */}
      <svg
        ref={svgRef}
        key={word}
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: '#e2ecff', stroke: 'currentColor', strokeWidth: 1.2, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' }}
      >
        <Variant />
      </svg>

      {/* Active word badge */}
      <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-black/55 backdrop-blur-sm border border-gray-700/40 px-3 py-1.5 rounded">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accentAlt }}></span>
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>{word}</span>
      </div>

      {/* Corner ticks */}
      <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-gray-400/60"></div>
      <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-orange-400/60"></div>
      <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-gray-400/60"></div>

      {/* Live CAD chip */}
      <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 bg-black/55 backdrop-blur-sm border border-orange-500/40 px-3 py-1 rounded text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>
        <span style={{ background: accentAlt }} className="w-1 h-1 rounded-full animate-pulse"></span>
        Live CAD · drawing
      </div>
    </div>
  );
};

export default HeroLineArt;
